import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Country, State } from '../../../types/country';
import { Checkbox, Col, DatePicker, Form, Input, message, Row, Select, Typography } from 'antd';
import dayjs from 'dayjs';
import { FormInstance, Rule } from 'antd/es/form';
import { CountryDropdownContainer } from '../country-dropdown/country-dropdown-container';
import { getEnvironmentVariable } from '../../shared/get-environment-variable';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
const { Title } = Typography;

interface PaymentDetailsProps {
  countries: Country[];
  cyberSourcePublicKey: string;
  form: FormInstance;
  setGeneratePaymentToken: Dispatch<SetStateAction<(() => Promise<void>) | undefined>>;
  displayTitle?: boolean;
}
interface TokenOptions {
  expirationMonth: string;
  expirationYear: string;
}
type PaymentTokenFormFieldType = {
  cardNumber?: string;
  securityCode?: string;
  expiration?: string;
};

const COLUMN_SPAN = 24 / 2;

export const PaymentDetails: React.FC<PaymentDetailsProps> = ({ displayTitle = true, ...props }) => {
  const [zipCodeRules, setZipCodeRules] = useState<Rule[]>([{ required: true, message: 'Please enter the ZIP/Postal code.' }]);
  const [flexMicroform, setFlexMicroform] = useState<any>(null);
  const [hasMounted, setHasMounted] = useState(false);
  const [agreeCheckboxValue, setAgreeCheckboxValue] = useState<boolean>(false);
  const [paymentTokenForm] = Form.useForm();
  
  const cyberSourceUrl = getEnvironmentVariable('VITE_CYBERSOURCE_FLEX_MICROFORM_URL');

  const [cardNumberValidationHelpText, setCardNumberValidationHelpText] = useState<string>('');
  const [securityCodeValidationHelpText, setSecurityCodeValidationHelpText] = useState<string>('');

  const createToken = (tokenOption: TokenOptions) => {
    return new Promise((resolve, reject) => {
      if (flexMicroform === null) {
        message.error('Flex Microform is not loaded.');
        reject(new Error('Flex Microform is not loaded.'));
        return;
      }

      flexMicroform.createToken(tokenOption, function (error: Error | null, token: string) {
        if (error) {
          console.log('CREATE TOKEN ERROR', error);
          reject(error);
        } else {
          console.log('TOKEN CREATED', token);
          props.form.setFieldsValue({ paymentInstrumentToken: token });
          resolve(token);
        }
      });
    });
  };

  const createFlexObj = (keyId: string, clearValidationText: (data: any, field: string) => void): void => {
    try {
      const flex = new (window as { [key: string]: any })['Flex'](keyId);

      // Setup styles for flex iframe
      const myStyles = {
        input: {
          'font-size': '14px',
          'font-family': 'helvetica, tahoma, calibri, sans-serif',
          color: '#555',
          'line-height': '30px',
        },
        ':focus': { color: 'black' },
        ':disabled': { cursor: 'not-allowed' },
        valid: { color: '#3c763d',  },
        invalid: { color: '#a94442' },
        '::placeholder': { color: '#A4A4A4' }
        
      };
      const microform = flex.microform({ styles: myStyles });
      console.log('MICROFORM', microform);
      setFlexMicroform(microform);

      // Create fields
      const number = microform.createField('number', {
        placeholder: 'Enter card number'
      });
      const securityCode = microform.createField('securityCode', {
        maxLength: 4,
        placeholder: '••••',
        type: 'password'

      });

      // Add event listeners
      number.on('change', function (data: any) {
        clearValidationText(data, 'number');
      });
      securityCode.on('change', function (data: any) {
        clearValidationText(data, 'securityCode');
      });

      // Load fields
      number.load('#card-number-container');
      securityCode.load('#securityCode-container');
    } catch (error) {
      console.log('MICROFORM ERROR', error);
    }
  };

  useEffect(() => {
    setHasMounted(true);
  }, [setHasMounted]);

  useEffect(() => {
    if (props.cyberSourcePublicKey && hasMounted && cyberSourceUrl) {
      console.log(`${window.location.origin.toString()}/scripts/microform-css.js`);

      const microformScript: HTMLScriptElement = document.createElement('script');
      const cssScript: HTMLScriptElement = document.createElement('script');
      cssScript.src = `${window.location.origin.toString()}/scripts/microform-css.js`;
      microformScript.src = cyberSourceUrl;
      microformScript.async = true;
      cssScript.defer = true;
      document.body.appendChild(microformScript);
      document.body.appendChild(cssScript);
      microformScript.onload = () => {
        createFlexObj(props.cyberSourcePublicKey || '', async function (data: any, field: string) {
          switch (field) {
            case 'number':
              if (data.valid === true) {
                setCardNumberValidationHelpText('');
              }
              break;
            case 'securityCode':
              if (data.valid === true) {
                setSecurityCodeValidationHelpText('');
              }
              break;
          }
        });
      };

      cssScript.onload = () => {
        console.log('css loaded');
      };

      return () => {
        console.log('scripts removed');
        document.body.removeChild(microformScript);
        document.body.removeChild(cssScript);
      };
    }
  }, [hasMounted, props.cyberSourcePublicKey, cyberSourceUrl]);

  useEffect(() => {
    if (flexMicroform) {
      props.setGeneratePaymentToken(() => async () => await onSubmitPaymentTokenForm(paymentTokenForm.getFieldsValue()));
    }
  }, [flexMicroform]);

  // const [form] = Form.useForm();
  const selectedCountry = Form.useWatch('country', props.form);

  const renderStateFormItem = () => {
    const states = props.countries?.find((country: Country) => country.countryCode === selectedCountry)?.states;
    if (states && states.length > 0) {
      return (
        <Form.Item label="State / Province" name="state" rules={[{ required: true, message: 'Please enter the state.' }]}>
          <Select
            optionFilterProp="children"
            showSearch
            filterOption={(input, option) => (option?.label ? option?.label.toString().toLowerCase().includes(input.toLowerCase()) : false)}
            options={states.map((d: State) => ({
              value: d.stateCode,
              label: d.stateName
            }))}
          />
        </Form.Item>
      );
    } else {
      return (
        <Form.Item label="Billing State / Province" name="state" rules={[{ required: true, message: 'Please enter the state.' }]}>
          <Input
            type="text"
            placeholder="Billing State / Province"
            // maxLength={CybersourceSpecs.billToAddressState.maxLength}
          />
        </Form.Item>
      );
    }
  };

  const onCountryChange = (value: string) => {
    props.form.resetFields(['state']);

    switch (value) {
      case 'US':
        setZipCodeRules([
          { required: true, message: 'Please enter the ZIP/Postal code' },
          {
            pattern: /^\d{5}(?:[-\s]\d{4})?$/,
            message: 'Please enter a valid ZIP/Postal code'
          }
        ]);
        break;
      case 'CA':
        setZipCodeRules([
          { required: true, message: 'Please enter the ZIP/Postal code' },
          {
            pattern: /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/,
            message: 'Please enter a valid ZIP/Postal code'
          }
        ]);
        break;
      default:
        setZipCodeRules([{ required: true, message: 'Please enter the ZIP/Postal code' }]);
        break;
    }
  };

  const paymentTokenFormFields = (
    <>
      <p>Payment must be made by credit card. We accept Visa, MasterCard, Discover, and American Express. Please enter the information for the credit card you will use for payment in the fields below.</p>
      <Form.Item<PaymentTokenFormFieldType>
        label="Card Number:"
        name="cardNumber"
        help={cardNumberValidationHelpText}
        rules={[{ required: true, message: 'Please provide the card number.' }]}
      >
        <div id="card-number-container" />
      </Form.Item>

      <Row gutter={[16, 8]}>
        <Col xs={24} sm={12}>
          <Form.Item<PaymentTokenFormFieldType>
            label="Card Expiration Date:"
            required={true}
            name="expiration"
            rules={[
              {
                required: true,
                message: 'Please enter the expiration date as shown on the card.'
              }
            ]}
          >
            <DatePicker.MonthPicker
              id="expirationMonthPicker"
              name="expirationMonthPicker"
              placeholder="Month-Year"
              format="MM-YYYY"
              disabledDate={(current: any) => {
                return current && current < dayjs().utc().endOf('day').subtract(1, 'day');
              }}
              className="w-full"
            ></DatePicker.MonthPicker>
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item<PaymentTokenFormFieldType> label="Security Code:" name="securityCode" rules={[{ required: true }]} help={securityCodeValidationHelpText}>
            <div id="securityCode-container" />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  const onSubmitPaymentTokenForm = async (values: PaymentTokenFormFieldType) => {
    const tokenOption: TokenOptions = {
      expirationMonth: dayjs(values.expiration).utc().format('MM'),
      expirationYear: dayjs(values.expiration).utc().format('YYYY')
    };

    try {
      await createToken(tokenOption);
    } catch (error) {
      message.error('An error occurred while creating the payment token.');
    }
  };

  // Display error if cybersource url is not defined
  if (!cyberSourceUrl) {
    return (
      <div style={{ color: 'red', margin: '1em 0' }}>
        Cybersource Flex Microform URL is not defined in environment variables
      </div>
    );
  }

  return (
    <div>
      {displayTitle && <Title level={3}>Payment Details</Title>}

      <Form form={paymentTokenForm} layout="vertical">
        {paymentTokenFormFields}
      </Form>

      <Form form={props.form} layout="vertical">
        <Row gutter={[16, 8]}>
          <Col xs={24} sm={12}>
            <Form.Item label="First Name on Card" name="billingFirstName" rules={[{ required: true, message: 'Please enter valid First Name' }]}>
              <Input placeholder="First Name on Card" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Last Name on Card " name="billingLastName" rules={[{ required: true, message: 'Please enter valid Last Name' }]}>
                <Input placeholder="Last Name on Card" />
              </Form.Item>
          </Col>
            <Form.Item name="paymentInstrumentToken" hidden>
              <Input />
            </Form.Item>
        </Row>

        <Row gutter={[30, 16]}>
          <Col span={COLUMN_SPAN * 2} xs={24}>
            <Form.Item
              label="Email Address"
              name="billingEmail"
              rules={[
                {
                  required: true,
                  message: 'Please enter a valid email address.'
                },
                {
                  type: 'email',
                  message: 'Please enter a valid email address.'
                }
              ]}
            >
              <Input type="text" maxLength={500} placeholder="Email Address" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[30, 16]}>
          <Col span={COLUMN_SPAN * 2} xs={24}>
          <CountryDropdownContainer 
            isCreditCardDetails={true}
            onChange={onCountryChange}
            label="Country" name="country" rules={[{ required: true, message: 'Please select the country.' }]} />
          </Col>
        </Row>

        <Row gutter={[30, 16]}>
          <Col span={COLUMN_SPAN * 2} xs={24}>
            <Form.Item
              label="Billing Address"
              name="address"
              rules={[
                {
                  required: true,
                  message: 'Please enter the billing street address.'
                }
              ]}
            >
              <Input placeholder="Billing Address" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[30, 16]}>
          <Col span={COLUMN_SPAN} xs={24}>
            <Form.Item
              label="Billing City"
              name="city"
              rules={[
                { required: true, message: 'Please enter the billing city.' },
                {
                  max: 500,
                  message: 'City name cannot exceed 500 characters.'
                }
              ]}
            >
              <Input placeholder="Billing City" />
            </Form.Item>
          </Col>
          <Col span={COLUMN_SPAN} xs={24}>
            {renderStateFormItem()}
          </Col>
        </Row>

        <Row gutter={[30, 16]}>
          <Col span={COLUMN_SPAN} xs={24}>
            <Form.Item label="Billing ZIP / Postal Code" name="billingPostalCode" rules={zipCodeRules}>
              <Input placeholder="Billing ZIP / Postal Code" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="agree"
          rules={[
            {
              validator: () => {
                return agreeCheckboxValue ? Promise.resolve() : Promise.reject(new Error('You must agree to the terms and conditions'));
              }
            }
          ]}
        >
          <Checkbox checked={agreeCheckboxValue} onChange={(e) => setAgreeCheckboxValue(e.target.checked)}>
          <p>I authorize this transaction to request that ECFMG create my profile and verify my identity. I agree that if this service cannot be provided, I will be refunded the fee minus a processing fee.</p>
          </Checkbox>
        </Form.Item>
      </Form>
    </div>
  );
};
